import { useMemo } from 'react';

import { useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';

import {
  Configuration,
  ConfigurationsApi,
  CreateConfigurationConfigurationsPostRequest,
  DeleteConfigurationConfigurationsIdDeleteRequest,
  EventsApi,
  PageConfigurationResponseSchema,
  PageEventSchema,
  UpdateConfigurationConfigurationsIdPutRequest,
} from '@ais-ui/api-spec/s2s';
import { getBasePath } from './getAISBasePath';
import {
  convertFieldsToDates,
  getAuthHeaderFromCredentials,
} from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';
import { EVENTS_PAGE_SIZE, fetchAllPages } from './fetchAllPages';
import { useBasePath } from '@ais-ui/custom-hooks';
import { useSession } from '@ais-ui/websockets';

/** Shared S2S base URL and auth headers for all S2S API calls. */
const useS2SConnection = () => {
  const credentials = useCredentials();
  const basePath = getBasePath();
  const { hostname } = useBasePath();
  const username = credentials?.username || '';
  const password = credentials?.password || '';
  const { sessionId } = useSession();

  const isLocal = hostname.includes('localhost');
  const serviceBasePath = isLocal
    ? `${basePath}api/ingress/event-detection`
    : `${basePath}aisweb/api/ingress/event-detection`;

  const headers: Record<string, string> = isLocal
    ? {
        'X-Application-Name': 'marinepulse',
        Authorization: getAuthHeaderFromCredentials(username, password),
      }
    : {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      };

  return { serviceBasePath, headers };
};

const useS2SRequestConfiguration = () => {
  const { serviceBasePath, headers } = useS2SConnection();
  return new Configuration({ basePath: serviceBasePath, headers });
};

interface S2SDateRange {
  start_datetime?: string;
  end_datetime?: string;
}

const formatDateRange = (from?: Date, to?: Date): S2SDateRange => {
  return {
    start_datetime: from?.toISOString(),
    end_datetime: to?.toISOString(),
  };
};

/**
 * The generated EventsApi calls `/events/current`, but the deployed backend
 * serves S2S events at `/s2s-events`. This fetch wrapper corrects the path
 * and appends `start_datetime` / `end_datetime` query params.
 *
 * It also captures the raw JSON items so fields not in the generated schema
 * (ack_state, ack_state_modified_by, ack_state_modified_ts, fired_time) can be
 * merged back after the generated deserializer runs. Raw items are stored per
 * page (keyed by the request's `page` param) so concurrent page requests never
 * overwrite each other's data.
 */
const createS2SEventsFetch = (
  dateRange?: S2SDateRange,
  rawItemsByPage?: Map<number, Record<string, unknown>[]>,
): typeof fetch => {
  return async (input, init) => {
    let url = typeof input === 'string' ? input : input.toString();
    url = url.replace('/events/current', '/s2s-events');

    if (dateRange?.start_datetime) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}start_datetime=${encodeURIComponent(dateRange.start_datetime)}`;
    }
    if (dateRange?.end_datetime) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}end_datetime=${encodeURIComponent(dateRange.end_datetime)}`;
    }

    const response = await fetch(url, init);

    if (rawItemsByPage) {
      const page = Number(/[?&]page=(\d+)/.exec(url)?.[1] ?? 1);
      try {
        const data = await response.clone().json();
        rawItemsByPage.set(page, data?.items ?? []);
      } catch {
        rawItemsByPage.set(page, []);
      }
    }

    return response;
  };
};

export const useS2SConfigurationApi = () => {
  const configuration = useS2SRequestConfiguration();
  const configurationsApi = useMemo(
    () => new ConfigurationsApi(configuration),
    [configuration],
  );
  return configurationsApi;
};

export interface S2SEventsQueryParams {
  from?: Date;
  to?: Date;
  configurationId?: number | null;
  size?: number;
}

export const useGetCurrentS2SEvents = (
  params: S2SEventsQueryParams = {},
  enabled = true,
): UseQueryResult<PageEventSchema> => {
  const { serviceBasePath, headers } = useS2SConnection();
  const apiClientWrapper = useApiClientWrapper();

  const dateRange = formatDateRange(params.from, params.to);
  const pageSize = params.size ?? EVENTS_PAGE_SIZE;

  return useQuery(
    {
      queryKey: [
        's2sEvents',
        params.from?.toISOString(),
        params.to?.toISOString(),
        params.configurationId,
        pageSize,
      ],
      queryFn: async () => {
        // Build per-execution state so fetchAllPages can request pages
        // concurrently: each page stores its raw JSON under its own page key,
        // so concurrent responses never overwrite each other.
        const rawItemsByPage = new Map<number, Record<string, unknown>[]>();
        const fetchApi = createS2SEventsFetch(dateRange, rawItemsByPage);
        const configuration = new Configuration({
          basePath: serviceBasePath,
          headers,
          fetchApi,
        });
        const eventsApi = apiClientWrapper(
          new EventsApi(configuration),
        ) as EventsApi;

        const fetchPage = async (page: number): Promise<PageEventSchema> => {
          const response = await eventsApi.getCurrentEventsEventsCurrentGet({
            configurationId: params.configurationId,
            page,
            size: pageSize,
          });

          convertFieldsToDates(response, [
            'items.createdAt',
            'items.modifiedAt',
            'items.activationTime',
            'items.deactivationTime',
          ]);

          // The generated deserializer drops fields not in the schema. Merge
          // back ack_state and fired_time from this page's raw response.
          const rawItems = rawItemsByPage.get(page) ?? [];
          for (let i = 0; i < (response.items?.length ?? 0); i++) {
            const raw = rawItems[i];
            if (!raw) continue;
            const item = response.items![i] as unknown as Record<
              string,
              unknown
            >;
            item['ack_state'] = raw['ack_state'] ?? null;
            item['ack_state_modified_by'] =
              raw['ack_state_modified_by'] ?? null;
            item['ack_state_modified_ts'] =
              raw['ack_state_modified_ts'] ?? null;
            item['fired_time'] = raw['fired_time'] ?? null;
          }

          for (const item of response.items ?? []) {
            if (item.severity) {
              item.severity = item.severity.toLowerCase();
            }
          }

          return response;
        };

        return fetchAllPages(fetchPage, { label: 'S2S events' });
      },
      enabled,
      retry: false,
    },
    's2s:listCurrent',
  );
};

export const useFetchAllS2SConfigurations = (
  enabled = true,
): UseQueryResult<PageConfigurationResponseSchema> => {
  const configurationsApi = useS2SConfigurationApi();

  return useQuery(
    {
      queryKey: ['s2sAllConfigurations'],
      queryFn: async () => {
        const response =
          await configurationsApi.getConfigurationsConfigurationsGet({});

        for (const item of response.items ?? []) {
          if (item.severity) {
            item.severity = item.severity.toLowerCase();
          }
        }

        return response;
      },
      enabled,
    },
    's2s:listConfigurations',
  );
};

export const usePostS2SConfiguration = () => {
  const configurationsApi = useS2SConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation(
    {
      mutationFn: (
        requestBody: CreateConfigurationConfigurationsPostRequest,
      ) => {
        return configurationsApi.createConfigurationConfigurationsPost(
          requestBody,
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['s2sAllConfigurations'],
        });
      },
    },
    's2s:create',
  );
};

export const useEditS2SConfiguration = () => {
  const configurationsApi = useS2SConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation(
    {
      mutationFn: (
        requestBody: UpdateConfigurationConfigurationsIdPutRequest,
      ) => {
        return configurationsApi.updateConfigurationConfigurationsIdPut(
          requestBody,
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['s2sAllConfigurations'],
        });
      },
    },
    's2s:update',
  );
};

export const useDeleteS2SConfiguration = () => {
  const configurationsApi = useS2SConfigurationApi();
  const queryClient = useQueryClient();
  return useMutation(
    {
      mutationFn: (
        requestBody: DeleteConfigurationConfigurationsIdDeleteRequest,
      ) => {
        return configurationsApi.deleteConfigurationConfigurationsIdDelete(
          requestBody,
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['s2sAllConfigurations'],
        });
      },
    },
    's2s:delete',
  );
};

// S2S doesn't have a dedicated enable/disable endpoint like grounding.
// To toggle enabled state, use useEditS2SConfiguration with the full config body.

export const useAcknowledgeS2SEvent = () => {
  const { serviceBasePath, headers } = useS2SConnection();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: async (eventId: number) => {
        const response = await fetch(
          `${serviceBasePath}/s2s-events/${eventId}/ack`,
          { method: 'POST', headers },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to acknowledge S2S event: ${response.status}`,
          );
        }

        return response.json();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['s2sEvents'] });
      },
    },
    's2s:acknowledge',
  );
};
